import { memo, useReducer } from 'react';

import { useFormik } from 'formik';
import { SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { StreamerWheelForm, validationSchema } from '../form';
import { StreamerWheelFormFields } from '../streamerWheelFormFields/StreamerWheelFormFields';
import { mapDtoToForm, mapFormToDto, streamerWheelTypeToLabel } from '../utils';

import css from './StreamerWheelCard.module.css';

import { streamerWheelService } from 'v2/api/streamerWheelService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { Switch } from 'v2/components/switch/Switch';
import { StreamerWheelModel } from 'v2/types/StreamerWheelModel';
import { handleHttpError } from 'v2/utils';

interface StreamerWheelCardProps {
  wheel: StreamerWheelModel;
  number: number;
  onRevalidate: VoidFunction;
}

export const StreamerWheelCard = memo<StreamerWheelCardProps>(({ wheel, number, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateStreamerWheel', wheel.id],
    streamerWheelService.updateStreamerWheel
  );

  const formik = useFormik<StreamerWheelForm>({
    initialValues: mapDtoToForm(wheel),
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        await triggerUpdate(mapFormToDto(values));
        onRevalidate();
        toast.success('Streamer Wheel updated successfully');
        handleClose();
      } catch (error) {
        handleHttpError(error);
      }
    },
    enableReinitialize: true,
  });

  const { trigger: triggerToggleActive } = useSWRMutation(
    ['setActiveStreamerWheel', wheel.id],
    streamerWheelService.setActiveStreamerWheel,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(
    ['deleteStreamerWheel', wheel.id],
    streamerWheelService.deleteStreamerWheel,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const handleToggleActive = () => {
    triggerToggleActive(!wheel.isActive);
  };

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  const typeLabel = streamerWheelTypeToLabel[wheel.type];

  return (
    <div className={css.streamerWheelCard}>
      <div className={css.streamerWheelCardHeader}>
        <div className={css.streamerWheelCardTitle}>
          <p className={css.streamerWheelCardNumber}>{number}</p>

          <div className={css.streamerWheelCardMeta}>
            <p className={css.streamerWheelCardName} title={wheel.name}>
              {wheel.name}
            </p>

            <p className={css.streamerWheelCardType}>{typeLabel}</p>
          </div>
        </div>

        <Switch name="isActive" checked={wheel.isActive} onChange={handleToggleActive} label="Active" />
      </div>

      <Divider />

      <div className={css.streamerWheelCardActions}>
        <Tooltip title="Edit" placement="top">
          <IconButton onClick={setOpen} color="warning">
            <SquarePen width={18} height={18} />
          </IconButton>
        </Tooltip>

        <Popconfirm message="Delete streamer wheel?" onConfirm={triggerDelete}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={18} height={18} />
            </IconButton>
          </Tooltip>
        </Popconfirm>

        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
          <DialogTitle>Edit</DialogTitle>

          <DialogContent>
            <form id="streamerWheelForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
              <StreamerWheelFormFields formik={formik} />
            </form>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button form="streamerWheelForm" loading={isMutating} type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
});
